import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  listVendors,
  getVendor,
  registerVendor,
  updateVendor,
  deleteVendor,
  getVendorRiskSummary,
  getVendorAlerts,
  uploadVendorDPA,
  listVendorDPADocuments,
} from "@/services/dpdpApi";

const VENDOR_CATEGORIES = [
  { value: "analytics", label: "Analytics" },
  { value: "payments", label: "Payments" },
  { value: "email", label: "Email" },
  { value: "cloud", label: "Cloud" },
  { value: "crm", label: "CRM" },
  { value: "marketing", label: "Marketing" },
  { value: "infrastructure", label: "Infrastructure" },
  { value: "other", label: "Other" },
];

const DATA_CATEGORIES = [
  { value: "aadhaar", label: "Aadhaar / Govt ID", risk: "high" },
  { value: "financial", label: "Financial Data", risk: "high" },
  { value: "health", label: "Health Records", risk: "high" },
  { value: "biometric", label: "Biometric", risk: "high" },
  { value: "precise_location", label: "Precise Location", risk: "high" },
  { value: "name", label: "Name", risk: "medium" },
  { value: "email", label: "Email", risk: "medium" },
  { value: "phone", label: "Phone Number", risk: "medium" },
  { value: "address", label: "Address", risk: "medium" },
  { value: "employment", label: "Employment Info", risk: "medium" },
  { value: "anonymised", label: "Anonymised Data", risk: "low" },
  { value: "aggregated", label: "Aggregated Data", risk: "low" },
];

function RiskBadge({ level }) {
  const styles = {
    high: "bg-red-100 text-red-700 border-red-300",
    medium: "bg-amber-100 text-amber-700 border-amber-300",
    low: "bg-green-100 text-green-700 border-green-300",
  };
  return (
    <Badge variant="outline" className={styles[level] || "bg-gray-100 text-gray-700"}>
      {level?.toUpperCase()} RISK
    </Badge>
  );
}

function DPAStatusBadge({ status }) {
  const styles = {
    valid: "bg-green-100 text-green-700",
    expiring_soon: "bg-amber-100 text-amber-700",
    expired: "bg-red-100 text-red-700",
    missing: "bg-red-100 text-red-700",
  };
  const labels = {
    valid: "DPA Valid",
    expiring_soon: "DPA Expiring",
    expired: "DPA Expired",
    missing: "No DPA",
  };
  return (
    <Badge variant="outline" className={styles[status] || "bg-gray-100 text-gray-700"}>
      {labels[status] || status}
    </Badge>
  );
}

function VendorForm({ onSubmit, initialData, onCancel }) {
  const [form, setForm] = useState({
    name: initialData?.name || "",
    category: initialData?.category || "other",
    contact_email: initialData?.contact_email || "",
    description: initialData?.description || "",
    website: initialData?.website || "",
    data_categories: initialData?.data_categories || [],
  });

  const toggleCategory = (val) => {
    setForm((f) => ({
      ...f,
      data_categories: f.data_categories.includes(val)
        ? f.data_categories.filter((c) => c !== val)
        : [...f.data_categories, val],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.contact_email || form.data_categories.length === 0) {
      toast.error("Name, contact email, and at least one data category are required.");
      return;
    }
    onSubmit(form);
  };

  const riskLevel = (() => {
    const cats = form.data_categories;
    const highRisk = DATA_CATEGORIES.filter((c) => c.risk === "high").map((c) => c.value);
    const medRisk = DATA_CATEGORIES.filter((c) => c.risk === "medium").map((c) => c.value);
    if (cats.some((c) => highRisk.includes(c))) return "HIGH";
    if (cats.some((c) => medRisk.includes(c))) return "MEDIUM";
    return "LOW";
  })();

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Vendor Name *</label>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Stripe Inc."
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Category *</label>
          <select
            className="w-full border rounded px-3 py-2 bg-background"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          >
            {VENDOR_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Contact Email *</label>
          <Input
            type="email"
            value={form.contact_email}
            onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
            placeholder="privacy@vendor.com"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Website</label>
          <Input
            value={form.website}
            onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
            placeholder="https://vendor.com"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          Data Categories Shared * <span className="text-xs text-muted-foreground">(auto-calculates risk)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {DATA_CATEGORIES.map((cat) => {
            const selected = form.data_categories.includes(cat.value);
            const color =
              cat.risk === "high" ? "bg-red-50 border-red-300 text-red-700" :
              cat.risk === "medium" ? "bg-amber-50 border-amber-300 text-amber-700" :
              "bg-green-50 border-green-300 text-green-700";
            return (
              <button
                key={cat.value}
                type="button"
                onClick={() => toggleCategory(cat.value)}
                className={`px-3 py-1 text-sm border rounded-full cursor-pointer transition-colors ${
                  selected ? `${color} border-2 font-semibold` : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                }`}
              >
                {cat.label} {cat.risk === "high" ? "🔴" : cat.risk === "medium" ? "🟡" : "🟢"}
              </button>
            );
          })}
        </div>
        <p className="text-sm mt-2 text-muted-foreground">
          Auto-calculated risk: <RiskBadge level={riskLevel.toLowerCase()} />
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <textarea
          className="w-full border rounded px-3 py-2 bg-background min-h-[80px]"
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="Brief description of vendor and data processing activities..."
        />
      </div>

      <div className="flex gap-3 justify-end">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        )}
        <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
          {initialData ? "Update Vendor" : "Register Vendor"}
        </Button>
      </div>
    </form>
  );
}

export default function VendorRegistryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [riskSummary, setRiskSummary] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [tab, setTab] = useState("list");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [dpaDocs, setDpaDocs] = useState([]);

  const loadVendors = async () => {
    try {
      setLoading(true);
      const data = await listVendors({ limit: 100 });
      setVendors(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error("Failed to load vendor registry");
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      const data = await getVendorAlerts();
      setAlerts(data);
    } catch {
      // Non-critical
    }
  };

  useEffect(() => {
    loadVendors();
    loadAlerts();
  }, []);

  const handleSubmit = async (formData) => {
    try {
      if (editingVendor) {
        await updateVendor(editingVendor.vendor_id, formData);
        toast.success("Vendor updated successfully");
      } else {
        await registerVendor(formData);
        toast.success("Vendor registered successfully");
      }
      setShowForm(false);
      setEditingVendor(null);
      loadVendors();
      loadAlerts();
    } catch (err) {
      toast.error(err?.message || "Failed to save vendor");
    }
  };

  const handleDelete = async (vendor) => {
    if (!confirm(`Deactivate vendor "${vendor.name}"?`)) return;
    try {
      await deleteVendor(vendor.vendor_id);
      toast.success("Vendor deactivated");
      loadVendors();
    } catch {
      toast.error("Failed to deactivate vendor");
    }
  };

  const openVendorDetail = async (vendor) => {
    try {
      const detail = await getVendor(vendor.vendor_id);
      const docs = await listVendorDPADocuments(vendor.vendor_id);
      setSelectedVendor(detail);
      setDpaDocs(docs || []);
      setTab("detail");
    } catch {
      toast.error("Failed to load vendor details");
    }
  };

  const handleUploadDPA = async () => {
    if (!selectedVendor) return;
    const dpoName = prompt("Enter DPO name signing the DPA:");
    if (!dpoName) return;
    try {
      await uploadVendorDPA(selectedVendor.vendor_id, dpoName);
      toast.success("DPA document recorded");
      const docs = await listVendorDPADocuments(selectedVendor.vendor_id);
      setDpaDocs(docs || []);
      loadVendors();
      loadAlerts();
    } catch {
      toast.error("Failed to upload DPA record");
    }
  };

  const totalAlerts = alerts ? alerts.total_alerts : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-slate-900/90 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <span className="text-2xl">🏢</span> Vendor DPA Registry
            </h1>
            <p className="text-slate-400 text-sm mt-0.5">
              Safeguard 6: Vendor Data Processing Agreements — DPDP Act Section 8(5)
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="border-indigo-500 text-indigo-400 hover:bg-indigo-950"
              onClick={() => navigate("/admin/compliance")}
            >
              ← Compliance Dashboard
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => { setShowForm(true); setEditingVendor(null); setTab("form"); }}
            >
              + Add Vendor
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Critical alert banner */}
        {totalAlerts > 0 && (
          <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
            <span className="text-2xl">🚨</span>
            <div>
              <p className="text-red-300 font-semibold">
                {totalAlerts} vendor DPA alert{totalAlerts > 1 ? "s" : ""} requiring DPO attention
              </p>
              {(alerts?.expired?.length > 0 || alerts?.missing_dpa?.length > 0) && (
                <p className="text-red-400 text-sm">
                  {alerts.expired.length} expired, {alerts.missing_dpa.length} missing —
                  webhook dispatches are blocked for these vendors
                </p>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto border-red-500 text-red-400 hover:bg-red-950"
              onClick={() => setTab("alerts")}
            >
              View Alerts
            </Button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-white">{total}</div>
              <div className="text-slate-400 text-sm mt-1">Total Vendors</div>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-red-400">
                {vendors.filter((v) => v.risk_level === "high").length}
              </div>
              <div className="text-slate-400 text-sm mt-1">High Risk</div>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-amber-400">
                {vendors.filter((v) => v.risk_level === "medium").length}
              </div>
              <div className="text-slate-400 text-sm mt-1">Medium Risk</div>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-green-400">
                {vendors.filter((v) => v.risk_level === "low").length}
              </div>
              <div className="text-slate-400 text-sm mt-1">Low Risk</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white/10 border border-white/10">
            <TabsTrigger value="list" className="data-[state=active]:bg-indigo-600">Vendor List</TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-indigo-600 relative">
              Alerts
              {totalAlerts > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {totalAlerts}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="dpa-gen" className="data-[state=active]:bg-indigo-600">
              Generate DPA Template
            </TabsTrigger>
            {showForm && (
              <TabsTrigger value="form" className="data-[state=active]:bg-indigo-600">
                {editingVendor ? "Edit Vendor" : "Add Vendor"}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Vendor List */}
          <TabsContent value="list">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : vendors.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-slate-400 mb-4">No vendors registered yet.</p>
                    <Button className="bg-indigo-600" onClick={() => setTab("form")}>Register First Vendor</Button>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10 text-left text-sm text-slate-400">
                        <th className="px-4 py-3 font-medium">Vendor</th>
                        <th className="px-4 py-3 font-medium">Category</th>
                        <th className="px-4 py-3 font-medium">Risk</th>
                        <th className="px-4 py-3 font-medium">DPA Status</th>
                        <th className="px-4 py-3 font-medium">DPA Expiry</th>
                        <th className="px-4 py-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendors.map((v) => (
                        <tr key={v.vendor_id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="px-4 py-3">
                            <div className="text-white font-medium">{v.name}</div>
                            <div className="text-slate-400 text-xs">{v.contact_email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="bg-white/5 text-slate-300 border-white/10">
                              {v.category}
                            </Badge>
                          </td>
                          <td className="px-4 py-3"><RiskBadge level={v.risk_level} /></td>
                          <td className="px-4 py-3">
                            <DPAStatusBadge status={v.dpa_status || (v.dpa_required && !v.dpa_expiry_date ? "missing" : "valid")} />
                          </td>
                          <td className="px-4 py-3 text-slate-300 text-sm">
                            {v.dpa_expiry_date
                              ? new Date(v.dpa_expiry_date).toLocaleDateString()
                              : v.dpa_required ? <span className="text-red-400">Not set</span> : "—"}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <Button size="sm" variant="ghost" className="text-indigo-400 hover:text-indigo-300 text-xs"
                                onClick={() => { setEditingVendor(v); setShowForm(true); setTab("form"); }}>
                                Edit
                              </Button>
                              <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white text-xs"
                                onClick={() => openVendorDetail(v)}>
                                View
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 text-xs"
                                onClick={() => handleDelete(v)}>
                                Deactivate
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DPO Alerts */}
          <TabsContent value="alerts">
            <div className="space-y-4">
              {alerts?.expired?.length > 0 && (
                <Card className="bg-red-950/30 border-red-500/30">
                  <CardHeader><CardTitle className="text-red-400 text-lg">🔴 Expired DPAs</CardTitle></CardHeader>
                  <CardContent>
                    {alerts.expired.map((e) => (
                      <div key={e.vendor_id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                        <div>
                          <p className="text-white font-medium">{e.vendor_name}</p>
                          <p className="text-red-400 text-sm">{e.reason}</p>
                        </div>
                        <RiskBadge level={e.risk_level} />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              {alerts?.missing_dpa?.length > 0 && (
                <Card className="bg-red-950/30 border-red-500/30">
                  <CardHeader><CardTitle className="text-red-400 text-lg">⚠️ Missing DPAs</CardTitle></CardHeader>
                  <CardContent>
                    {alerts.missing_dpa.map((e) => (
                      <div key={e.vendor_id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                        <div>
                          <p className="text-white font-medium">{e.vendor_name}</p>
                          <p className="text-red-400 text-sm">No DPA document on file</p>
                        </div>
                        <RiskBadge level={e.risk_level} />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              {alerts?.expiring_soon?.length > 0 && (
                <Card className="bg-amber-950/30 border-amber-500/30">
                  <CardHeader><CardTitle className="text-amber-400 text-lg">🟡 Expiring Soon</CardTitle></CardHeader>
                  <CardContent>
                    {alerts.expiring_soon.map((e) => (
                      <div key={e.vendor_id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                        <div>
                          <p className="text-white font-medium">{e.vendor_name}</p>
                          <p className="text-amber-400 text-sm">Expires in {e.days_until_expiry} days</p>
                        </div>
                        <RiskBadge level={e.risk_level} />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              {totalAlerts === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <span className="text-4xl">✅</span>
                  <p className="mt-4">All vendor DPAs are valid and up to date.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* DPA Generator */}
          <TabsContent value="dpa-gen">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">📄 Generate DPA Template</CardTitle>
                <p className="text-slate-400 text-sm">
                  Generate a DPDP Act 2023 compliant Data Processing Agreement document. Print to PDF for signing.
                </p>
              </CardHeader>
              <CardContent>
                <Button className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => navigate("/admin/vendors/dpa/new")}>
                  Open DPA Generator →
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Add/Edit Form */}
          {showForm && (
            <TabsContent value="form">
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">
                    {editingVendor ? `Edit Vendor: ${editingVendor.name}` : "Register New Vendor"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <VendorForm
                    initialData={editingVendor}
                    onSubmit={handleSubmit}
                    onCancel={() => { setShowForm(false); setEditingVendor(null); setTab("list"); }}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Vendor Detail */}
          <TabsContent value="detail">
            {selectedVendor && (
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white text-xl">{selectedVendor.name}</CardTitle>
                      <p className="text-slate-400 text-sm mt-1">{selectedVendor.category} · {selectedVendor.contact_email}</p>
                    </div>
                    <div className="flex gap-2">
                      <RiskBadge level={selectedVendor.risk_level} />
                      <DPAStatusBadge status={selectedVendor.dpa_status || "missing"} />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-400 uppercase">Data Categories</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(selectedVendor.data_categories || []).map((c) => (
                          <Badge key={c} variant="outline" className="bg-white/5 text-slate-300 text-xs">{c}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 uppercase">DPA Expiry</p>
                      <p className="text-white mt-1">
                        {selectedVendor.dpa_expiry_date
                          ? new Date(selectedVendor.dpa_expiry_date).toLocaleDateString()
                          : "Not set"}
                      </p>
                    </div>
                  </div>
                  {selectedVendor.description && (
                    <div>
                      <p className="text-xs text-slate-400 uppercase">Description</p>
                      <p className="text-slate-300 mt-1">{selectedVendor.description}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-white mb-2">DPA Documents</p>
                    {dpaDocs.length === 0 ? (
                      <p className="text-slate-400 text-sm">No DPA documents uploaded.</p>
                    ) : (
                      <ul className="space-y-2">
                        {dpaDocs.map((doc) => (
                          <li key={doc.dpa_doc_id} className="flex items-center gap-3 text-slate-300 text-sm bg-white/5 rounded px-3 py-2">
                            <span>📄</span>
                            <span>{doc.filename}</span>
                            <span className="text-slate-500 text-xs ml-auto">
                              Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Button size="sm" variant="outline" className="mt-3 border-indigo-500 text-indigo-400"
                      onClick={handleUploadDPA}>
                      + Record DPA Document
                    </Button>
                  </div>
                  <Button variant="outline" className="text-slate-400" onClick={() => { setSelectedVendor(null); setTab("list"); }}>
                    ← Back to List
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
